import { CheckBox, CheckBoxOutlineBlank } from "@mui/icons-material";
import { Autocomplete, Box, Checkbox, Chip, ListItem, ListItemText, Skeleton, TextField } from "@mui/material";
import React, { useEffect, useState, useRef } from "react";
import { useProductsContext } from "../Contexts/productsContext";
import { SKU_LOCAL_STORAGE_KEY } from "../Utilities/constants.ts";

const icon = <CheckBoxOutlineBlank fontSize="small" />;
const checkedIcon = <CheckBox fontSize="small" />;

export default function ProductMultiSelection() {
    const {
        products,
        isReady,
        selectedProducts,
        setSelectedProducts,
    } = useProductsContext();
    const [filteredProducts, setFilteredProducts] = useState([]);
    const [inputValue, setInputValue] = useState("");
    const [selectAll, setSelectAll] = useState(false);
    const isInitialMount = useRef(true);

    useEffect(() => {
        if (isReady) {
            setFilteredProducts(products || []);
        }
    }, [isReady]);

    useEffect(() => {
        // Skip on initial mount to allow page to restore from localStorage first
        if (isInitialMount.current) {
            isInitialMount.current = false;
            return;
        }

        if (selectedProducts && selectedProducts.length > 0) {
            localStorage.setItem(SKU_LOCAL_STORAGE_KEY, JSON.stringify(selectedProducts));
        } else {
            // Clear localStorage when products are cleared
            localStorage.removeItem(SKU_LOCAL_STORAGE_KEY);
        }
    }, [selectedProducts]);

    const handleSelectAllChange = () => {
        if (selectAll) {
            setSelectedProducts([]);
        } else {
            const filteredProducts = products.filter(item =>
                item.description.toLowerCase().includes(inputValue.toLowerCase()) ||
                item.sku.toLowerCase().includes(inputValue.toLowerCase())
            );
            setSelectedProducts(filteredProducts);
        }

        setSelectAll(!selectAll);
    };

    const handleChange = (event, value) => {
        if (value.length === products.length) {
            setSelectAll(true);
        } else {
            setSelectAll(false);
        }

        setSelectedProducts(value);
    };
    return (
        <React.Fragment>
            {isReady ? (
                <Autocomplete
                    multiple
                    limitTags={1}
                    disablePortal
                    filterOptions={(options, state) => {
                        const inputValue = state.inputValue;

                        const matchingStrings = options.filter(item =>
                            item.description.toLowerCase().includes(inputValue.toLowerCase()) ||
                            item.sku.toLowerCase().includes(inputValue.toLowerCase())
                        );
                        matchingStrings.unshift('Select All');

                        return matchingStrings;
                    }}
                    id="combo-box-product"
                    getOptionLabel={(option) => `${option.sku} - ${option.description}`}
                    options={filteredProducts}
                    disableCloseOnSelect
                    inputValue={inputValue}
                    isOptionEqualToValue={(option, value) => option.sku === value.sku}
                    onInputChange={(event, value, reason) => {
                        if (event && event.type === 'blur') {
                            setInputValue('');
                        } else if (reason !== 'reset') {
                            setInputValue(value);
                        }
                    }}
                    renderOption={(props, option) => {
                        if (option === 'Select All') {
                            return (
                                <ListItem {...props} key="select-all" onClick={handleSelectAllChange}>
                                    <Checkbox checked={selectAll} />
                                    <ListItemText primary="Select All" />
                                </ListItem>
                            );
                        }

                        const isSelected = selectedProducts?.some(
                            selectedProduct => selectedProduct.sku === option.sku
                        );

                        return (
                            <ListItem {...props} key={option.sku}>
                                <Checkbox
                                    icon={icon}
                                    checkedIcon={checkedIcon}
                                    style={{ marginRight: 8 }}
                                    checked={isSelected}
                                />
                                <ListItemText primary={`${option.sku} - ${option.description}`} />
                            </ListItem>
                        )
                    }}
                    sx={{
                        width: 350,
                        '&.MuiAutocomplete-hasPopupIcon.MuiAutocomplete-hasClearIcon .MuiOutlinedInput-root': {
                            paddingRight: '20px',
                        },
                    }}
                    renderInput={(params) => (
                        <TextField {...params} label="Select Products" />
                    )}
                    renderTags={(value, getTagProps) => {
                        const visibleChips = value.slice(0, 1);
                        const hiddenCount = value.length - visibleChips.length;

                        return (
                            <Box display={"flex"} alignItems={"center"} flexWrap={"nowrap"} overflow={"hidden"}>
                                {visibleChips?.map((option, index) => (
                                    <Chip
                                        style={{ width: "230px" }}
                                        label={`${option.sku} - ${option.description}`}
                                        {...getTagProps({ index })}
                                        key={option.sku}
                                    />
                                ))}

                                {hiddenCount > 0 && (
                                    <Chip
                                        label={`+${hiddenCount}`}
                                    />
                                )}
                            </Box>
                        );
                    }}
                    value={selectedProducts || []}
                    onChange={handleChange}
                />
            ) : (
                <Skeleton variant="rectangular" width={350} height={50} />
            )}
        </React.Fragment>
    );
}